<template>
  <v-card>
    <v-simple-table dense>
      <template #default>
        <tbody>
          <tr v-if="displayField('name')">
            <td>
              {{ $t("fund.data.name") }}
            </td>
            <td>
              {{ fund.name }}
            </td>
          </tr>

          <tr v-if="displayField('name') && fund.assetManagement">
            <td>
              {{ $t("fund.data.assetManagement.title") }}
            </td>
            <td>
              {{ $t("fund.data.assetManagement.description") }}
            </td>
          </tr>

          <tr v-if="displayField('fof') && fund.fof">
            <td>
              {{ $t("fund.data.fof.title") }}
            </td>
            <td>
              {{ $t("fund.data.fof.description") }}
            </td>
          </tr>

          <tr v-if="displayField('company.name')">
            <td>
              {{ $t("fund.data.company.name") }}
            </td>
            <td>
              {{ fund.user.company.address.name }}
            </td>
          </tr>

          <template v-if="!(fund.assetClasses && fund.assetClasses.length)">
            <tr>
              <td>
                {{ $t("fund.data.imported.title") }}
              </td>
              <td>
                {{ $t("fund.data.imported.description") }}
              </td>
            </tr>
          </template>

          <template v-else>
            <tr v-if="displayField('assetClasses')">
              <td>
                {{ $t("fund.data.assetClasses") }}
              </td>
              <td>
                {{ fund.assetClasses.map((val) => $t(`fund.assetClasses.${val}`)).join(", ") }}
              </td>
            </tr>

            <tr v-if="displayField('assetClassesComment') && fund.assetClassesComment">
              <td>
                {{ $t("fund.data.assetClassesComment") }}
              </td>
              <td>
                {{ fund.assetClassesComment }}
              </td>
            </tr>

            <tr v-if="displayField('assetManager') && fund.assetManager">
              <td>
                {{ $t("fund.data.assetManager") }}
              </td>
              <td>
                {{ fund.assetManager }}
              </td>
            </tr>

            <tr v-if="displayField('dependance') && fund.dependance">
              <td>
                {{ $t("fund.data.dependance") }}
              </td>
              <td>
                {{ fund.dependance }}
              </td>
            </tr>

            <tr v-if="displayField('isins')">
              <td>
                {{ $t("fund.data.isins") }}
              </td>
              <td>
                {{ fund.isins.join(", ") }}
              </td>
            </tr>

            <tr v-if="displayField('benchmark')">
              <td>
                {{ $t("fund.data.benchmark") }}
              </td>
              <td>
                {{ fund.benchmark }}
              </td>
            </tr>

            <tr v-if="displayField('fundType')">
              <td>
                {{ $t("fund.data.fundType") }}
              </td>
              <td>
                {{ fund.fundType }}
              </td>
            </tr>

            <tr v-if="displayField('investmentApproaches')">
              <td>
                {{ $t("fund.data.investmentApproaches") }}
              </td>
              <td>
                {{ fund.investmentApproaches.map((val) => $t(`fund.investmentApproaches.${val}`)).join(", ") }}
              </td>
            </tr>

            <tr v-if="displayField('tranches')">
              <td>
                {{ $t("fund.data.tranches") }}
              </td>
              <td>
                {{ fund.tranches.map((val) => $t(`fund.tranches.${val}`)).join(", ") }}
              </td>
            </tr>

            <tr v-if="displayField('tranchesComment') && fund.tranchesComment !== null">
              <td>
                {{ $t("fund.data.tranchesComment") }}
              </td>
              <td>
                {{ fund.tranchesComment }}
              </td>
            </tr>

            <tr v-if="displayField('performanceFee') && fund.performanceFee">
              <td>
                {{ $t("fund.data.performanceFee.title") }}
              </td>
              <td>
                {{ $t("fund.data.performanceFee.description") }}
              </td>
            </tr>

            <tr v-if="displayField('rter') && fund.rter">
              <td>
                {{ $t("fund.data.rter.title") }}
              </td>
              <td>
                {{ $t("fund.data.rter.description") }}
              </td>
            </tr>

            <tr v-if="displayField('privateInvestors') && fund.privateInvestors">
              <td>
                {{ $t("fund.data.privateInvestors.title") }}
              </td>
              <td>
                {{ $t("fund.data.privateInvestors.description") }}
              </td>
            </tr>

            <tr v-if="displayField('salesCountries')">
              <td>
                {{ $t("fund.data.salesCountries") }}
              </td>
              <td>
                {{ fund.salesCountries.map((val) => $t(`countries.${val}`)).join(", ") }}
              </td>
            </tr>

            <tr v-if="displayField('investmentRegion')">
              <td>
                {{ $t("fund.data.investmentRegion") }}
              </td>
              <td>
                {{ $t(`fund.investmentRegions.${fund.investmentRegion}`) }}
              </td>
            </tr>

            <tr v-if="displayField('investmentRegionComment') && fund.investmentRegionComment">
              <td>
                {{ $t("fund.data.investmentRegionComment") }}
              </td>
              <td>
                {{ fund.investmentRegionComment }}
              </td>
            </tr>

            <tr v-if="displayField('volume')">
              <td>
                {{ $t("fund.data.volume") }}
              </td>
              <td>
                <BaseNumeral :number="fund.volume" format="0,0[.][0000]" />
              </td>
            </tr>

            <tr v-if="displayField('currency')">
              <td>
                {{ $t("fund.data.currency") }}
              </td>
              <td>
                {{ fund.currency }}
              </td>
            </tr>

            <tr v-if="displayField('launchDate')">
              <td>
                {{ $t("fund.data.launchDate") }}
              </td>
              <td>
                {{ new Date(fund.launchDate).toLocaleDateString() }}
              </td>
            </tr>

            <tr v-if="displayField('language')">
              <td>
                {{ $t("fund.data.language") }}
              </td>
              <td>
                {{ fund.language }}
              </td>
            </tr>
          </template>

          <tr v-if="displayField('language') && fund.referenceFund">
            <td>
              {{ $t("fund.data.referenceFund") }}
            </td>
            <td>
              {{ fund.referenceFund.name }}
              ({{ fund.referenceFund.auditPeriod.year }})
            </td>
          </tr>

          <tr v-if="displayField('language') && fund.referencingFunds.length">
            <td>
              {{ $t("fund.data.referencingFunds") }}
            </td>
            <td>
              {{ fund.referencingFunds.map((val) => val.name).join(", ") }}
            </td>
          </tr>

          <tr v-if="target">
            <td>
              {{ $t("minimalRequirement.Zielsetzung2024.name") }}
            </td>
            <td>
              <Markdown :value="target" />
            </td>
          </tr>

          <tr v-if="displayField('disclosure')">
            <td>
              {{ $t("fund.data.disclosure") }}
            </td>
            <td>
              {{ $t(`disclosure.${fund.disclosure}`) }}
            </td>
          </tr>

          <tr v-if="displayField('disclosureComment')">
            <td>
              {{ $t("fund.data.disclosureComment") }}
            </td>
            <td>
              {{ fund.disclosureComment }}
            </td>
          </tr>

          <tr v-if="displayField('thirdPartyComment')">
            <td>
              {{ $t("fund.data.thirdPartyComment") }}
            </td>
            <td>
              {{ fund.thirdPartyComment }}
            </td>
          </tr>

          <tr v-if="displayField('comment')">
            <td>
              {{ $t("fund.data.comment") }}
            </td>
            <td>
              {{ fund.comment }}
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>
  </v-card>
</template>

<script>
import { displayFundField } from "@fng/common/fund/displayFundField";

export default {
  props: {
    fund: {
      type: Object,
      required: true,
    },
  },

  computed: {
    target() {
      return this.fund.minimalRequirements?.find((req) => req.requirementId === "Zielsetzung2024")?.result?.description;
    },
  },

  methods: {
    displayField(field) {
      return displayFundField(this.fund, field);
    },
  },
};
</script>
